// Reading and writing a binary reference (ADR-034): the picker's value, and the Source or In of a service model.
import type { BinaryReference, FieldValue, ServiceProjection } from '@/types'

/** A reference to nothing. Each call returns its own `path` array, so one caller's edit reaches no other. */
export function emptyReference(): BinaryReference {
  return { serviceId: '', projection: 'Output', path: [] }
}

export const EMPTY_REFERENCE: BinaryReference = emptyReference()

/** The reference a source field holds. A source that is unset, or holds an expression, reads as empty. */
export function referenceOf(value: FieldValue | undefined): BinaryReference {
  return value?.$type === 'binary' ? value.reference : emptyReference()
}

// Scope projection segment (lowercase) to wire enum (PascalCase). Only the two projections that carry content.
const PROJECTION_BY_SEGMENT: Record<string, ServiceProjection> = { output: 'Output', raw: 'Raw' }

/**
 * A scope path "services.<id>.<projection>.<member>..." as a reference. Service ids and the two projections
 * hold no dot, and the picker offers no bracket path, so splitting on "." is exact. Undefined for any path that
 * is not under a service's output or raw.
 */
export function referenceFromScopePath(scopePath: string): BinaryReference | undefined {
  const [root, serviceId, projectionSegment, ...path] = scopePath.split('.')
  const projection = PROJECTION_BY_SEGMENT[projectionSegment]
  if (root !== 'services' || !serviceId || !projection) return undefined
  return { serviceId, projection, path }
}

/** The scope path of a reference, so the tree can highlight it. Undefined when nothing is chosen. */
export function scopePathOf(reference: BinaryReference): string | undefined {
  if (reference.serviceId === '') return undefined
  return ['services', reference.serviceId, reference.projection.toLowerCase(), ...reference.path].join('.')
}

/** What the picker shows for a chosen reference: the service's label, "raw" for a raw reference, then the path. */
export function referenceCrumbs(reference: BinaryReference, serviceLabel: string): string[] {
  const projection = reference.projection === 'Raw' ? ['raw'] : []
  return [serviceLabel, ...projection, ...reference.path]
}
